import React from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { CredusText, CredusWrapper } from 'ui';
import { ProctoFolder } from '../../../../assets/images/svgs';
import { useNavigate } from 'react-router';
import { CandidateTestDetails } from '../../report-models/report.interface';

interface ReportTableProps {
  selectedRowKeys: React.Key[];
  setSelectedRowKeys: React.Dispatch<React.SetStateAction<React.Key[]>>;
  tableData: CandidateTestDetails[];
}
interface GradeWrapperProps {
  bg?: string;
  padding?: string;
  radius?: string;
  width?: string;
}

const ReportTable: React.FC<ReportTableProps> = ({
  selectedRowKeys,
  setSelectedRowKeys,
  tableData,
}) => {
  const navigate = useNavigate();
  const onSelectCard = (item: CandidateTestDetails) => () => {
    const path = item?.candidateResultId;
    if(item?.candidateId) sessionStorage.setItem("candidateId", `${item.candidateId}`);
    navigate(`/report/${path}`);
  };

  const data: CandidateTestDetails[] = tableData?.map(
    (item: CandidateTestDetails, i: React.Key) => ({
      ...item,
      key: i,
      candidate: item?.candidate,
      grade: item?.grade,
      memberID: item?.memberID,
      resultUploadedStatus: item?.resultUploadedStatus,
      totalMark: item.totalMark,
    })
  );

  const columns: ColumnsType<CandidateTestDetails> = [
    {
      title: 'Candidate',
      dataIndex: 'candidate',
    },
    {
      title: 'Member ID',
      dataIndex: 'memberID',
    },
    {
      title: 'Total Mark',
      dataIndex: 'totalMark',
    },
    {
      title: (
        <>
          <span
            style={{
              marginLeft: 20,
            }}
          >
            Grade
          </span>
        </>
      ),
      dataIndex: 'address',
      render: (text, record) => {
        return (
          <GradeWrapper
            width="80px"
            radius="30px"
            padding="4.5px 12px"
            bg={
              record?.grade === 'failed'
                ? '#FFF1EF'
                : record?.grade === 'good'
                ? '#EEFBEF'
                : '#E7E7E7'
            }
          >
            <CredusText
              align="center"
              size="xs"
              weight="medium"
              color={
                record?.grade === 'failed'
                  ? '#FC391D'
                  : record?.grade === 'good'
                  ? '#00AA4E'
                  : '#000000'
              }
            >
              {record?.grade}
            </CredusText>
          </GradeWrapper>
        );
      },
    },
    {
      title: 'Proctoring Data',
      dataIndex: 'address',
      render: () => {
        return (
          <CredusWrapper
            w="118px"
            padding="6px 12px"
            radius="6px"
            bg="#F7F9FC"
            display="flex"
            gap="12px"
            align="center"
          >
            <ProctoFolder />
            <CredusText align="center" size="xs" weight="medium">
              View Data
            </CredusText>
          </CredusWrapper>
        );
      },
    },
    {
      title: 'Result Upload Status',
      dataIndex: 'address',
      render: (text, record) => {
        return (
          <GradeWrapper
            width="99px"
            radius="6px"
            padding="5px 12px"
            bg={
              record?.resultUploadedStatus === 'Pending'
                ? '#FFF5E2'
                : record?.resultUploadedStatus === 'Uploaded'
                ? '#EEFBEF'
                : record?.resultUploadedStatus === 'Suspended'
                ? '#FFF1EF'
                : '#E7E7E7'
            }
          >
            <CredusText
              align="center"
              size="xs"
              weight="medium"
              color={
                record?.resultUploadedStatus === 'Pending'
                  ? '#DE9400'
                  : record?.resultUploadedStatus === 'Uploaded'
                  ? '#00AA4E'
                  : record?.resultUploadedStatus === 'Suspended'
                  ? '#FC391D'
                  : '#000000'
              }
            >
              {record?.resultUploadedStatus}
            </CredusText>
          </GradeWrapper>
        );
      },
    },
    {
      title: 'Action',
      dataIndex: 'address',
      render: (text, record) => {
        return (
          <ActionRelative>
            <BsThreeDotsVertical />
            <ActionAbsolute>
              <DropContent>
                <CredusText
                  onClick={onSelectCard(record)}
                  size="xs"
                  weight="medium"
                >
                  View details
                </CredusText>
              </DropContent>
              <DropContent>
                <CredusText size="xs" weight="medium">
                  Upload result
                </CredusText>
              </DropContent>
            </ActionAbsolute>
          </ActionRelative>
        );
      },
    },
  ];
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <TableWrap>
      {data && (
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={data}
        />
      )}
    </TableWrap>
  );
};

export default ReportTable;

const TableWrap = styled.div`
  margin-top: 1.875rem;
  .ant-table-thead .ant-table-cell {
    background: #ffeae7 !important;
  }
  td {
    border-bottom: none !important;
  }
`;

const ActionAbsolute = styled.div`
  display: none;
  flex-direction: column;
  align-items: center;
  padding: 5px;
  gap: 5px;
  top: 0;
  right: -48px;
  position: absolute;
  width: 122px;
  background: rgb(255, 255, 255);
  border: 1px solid rgb(224, 222, 247);
  box-shadow: rgba(16, 10, 85, 0.1) 0px 10px 10px;
  border-radius: 5px;
  z-index: 1;
`;
const ActionRelative = styled.div`
  position: relative;
  &:hover ${ActionAbsolute} {
    display: flex;
  }
`;

const DropContent = styled.div`
  padding: 10px 16px;
  cursor: pointer;
  transition: all 0.25s ease-in-out;
  border-radius: 8px;
  &:hover {
    background: #f0effb;
  }
`;
const GradeWrapper = styled.div<GradeWrapperProps>`
  padding: ${({ padding }) => padding};
  text-align: center;
  background: ${({ bg }) => bg};
  border-radius: ${({ radius }) => radius};
  width: ${({ width }) => width};
`;
